import React, { useState, useEffect, useCallback, useContext } from "react";
import Select from "react-select";
import Modal from "react-modal";
import AdvisorCard from "../components/AdviserCard";
import FormForWorker from "../components/FormForWorker";
import Spinner from "../components/Spinner";
import {
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaTimes,
  FaUserFriends,
  FaSync,
  FaArrowUp,
} from "react-icons/fa";
import axiosInstance from "../axiosInstance";
import debounce from "lodash/debounce";
import AuthContext from "../AuthContext";
import { useNavigate } from "react-router-dom"; // For navigation

// Custom styles for Select component
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "38px",
    height: "auto",
    borderColor: state.isFocused ? "#4a90e2" : provided.borderColor,
    boxShadow: state.isFocused ? "0 0 0 1px #4a90e2" : provided.boxShadow,
    "&:hover": {
      borderColor: "#4a90e2",
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e0e0e0",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#333",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#333",
    ":hover": {
      backgroundColor: "#e0e0e0",
      color: "#f00",
    },
  }),
};

const FindWork = () => {
  const navigate = useNavigate();

  // Context
  const { isLoggedOut } = useContext(AuthContext);

  // State declarations
  const [showForm, setShowForm] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [advisers, setAdvisers] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [badgeOptions, setBadgeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [availabilityOptions, setAvailabilityOptions] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationInput, setLocationInput] = useState("");
  const [jobInput, setJobInput] = useState("");
  const [certificateInput, setCertificateInput] = useState("");
  const [filteredLoading, setFilteredLoading] = useState(false);
  const [signinRegisterModalOpen, setSigninRegisterModalOpen] = useState(false);
  const [showButton, setShowButton] = useState(false);

  // Fetch data from the backend
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const locationOptionsResponse = await axiosInstance.get(`/api/suburbs`);
      const formattedLocations = locationOptionsResponse.data.map(
        (location) => ({ value: location, label: location })
      );
      setLocationOptions(formattedLocations);

      const certificateOptionsResponse = await axiosInstance.get(
        `/api/certificateOptions`
      );
      const formattedBadgeOptions =
        certificateOptionsResponse.data.CertificateOptions.map((certi) => ({
          value: certi,
          label: certi,
        }));
      setBadgeOptions(formattedBadgeOptions);

      // Make this hardcoded instead of fetching from the backend
      const formattedAvailabilityOptions = [
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
        { value: "Sunday", label: "Sunday" },
      ];
      setAvailabilityOptions(formattedAvailabilityOptions);

      //wait for 3 seconds before fetching the advisers
      const advisersResponse = await axiosInstance.get(
        `/api/findwork/advisers`
      );
      if (!advisersResponse.data || advisersResponse.data.length === 0) {
        if (!isFilterApplied()) {
          window.location.reload(); // Reload the pa
        }
      } else {
        setAdvisers(advisersResponse.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificateOptions = async (inputValue) => {
    try {
      const response = await axiosInstance.get(`/api/certificateOptions`, {
        params: { query: inputValue },
      });
      const formattedBadges = response.data.CertificateOptions.map((badge) => ({
        value: badge,
        label: badge,
      }));
      setBadgeOptions(formattedBadges);
    } catch (error) {
      console.error("Error fetching certificate options:", error);
    }
  };

  const debouncedFetchCertificates = useCallback(
    debounce(fetchCertificateOptions, 300),
    []
  );

  useEffect(() => {
    debouncedFetchCertificates(certificateInput);
  }, [certificateInput, debouncedFetchCertificates]);

  const fetchJobOptions = async (inputValue) => {
    try {
      const response = await axiosInstance.get(`/api/jobRoles/getRoles`, {
        params: { query: inputValue },
      });
      const formattedJobs = response.data.JobRoles.map((job) => ({
        value: job,
        label: job,
      }));
      setJobOptions(formattedJobs);
    } catch (error) {
      console.error("Error fetching job options:", error);
    }
  };

  const debouncedFetchJobs = useCallback(debounce(fetchJobOptions, 300), []);

  useEffect(() => {
    debouncedFetchJobs(jobInput);
  }, [jobInput, debouncedFetchJobs]);

  // Debounced function to fetch locations based on input
  const fetchLocationOptions = async (inputValue) => {
    try {
      const response = await axiosInstance.get(`/api/suburbs`, {
        params: { query: inputValue },
      });
      const formattedLocations = response.data.map((location) => ({
        value: location,
        label: location,
      }));
      setLocationOptions(formattedLocations);
    } catch (error) {
      console.error("Error fetching location options:", error);
    }
  };

  const debouncedFetchLocations = useCallback(
    debounce(fetchLocationOptions, 300),
    []
  );

  useEffect(() => {
    debouncedFetchLocations(locationInput);
  }, [locationInput, debouncedFetchLocations]);

  const handleAdvertiseClick = () => {
    axiosInstance.get("/api/askforjobcount");
    if (isLoggedOut) {
      showSignRegisterInModal();
      return;
    } else {
      setShowForm(true);
    }
  };

  const closeFormModal = () => {
    setShowForm(false);
  };

  const closeMessageModal = () => {
    setShowMessageModal(false);
  };

  const handleFormSubmit = async (data) => {
    try {
      await axiosInstance.post(`/api/findwork/submit`, data);
      setMessage("Data submitted successfully");
      setMessageType("success");
    } catch (error) {
      setMessage("Error submitting data");
      setMessageType("error");
    } finally {
      closeFormModal();
      setShowMessageModal(true);
    }
  };

  useEffect(() => {
    if (!loading) {
      setFilteredLoading(true);
      setTimeout(() => setFilteredLoading(false), 1000); // Simulate a delay for loading filtered workers
    }
  }, [
    selectedJobs,
    selectedBadges,
    selectedAvailability,
    selectedLocations,
    loading,
  ]);

  const filteredWorkers = advisers.filter((worker) => {
    const jobMatch =
      selectedJobs.length === 0 ||
      selectedJobs.some((job) => job.value === worker.job);
    const badgeMatch =
      selectedBadges.length === 0 ||
      selectedBadges.every((badge) =>
        worker.certificates.includes(badge.value)
      );
    const availabilityMatch =
      selectedAvailability.length === 0 ||
      selectedAvailability.every((day) => worker.availability[day.value]);

    const locationMatch =
      selectedLocations.length === 0 ||
      selectedLocations.some((location) => {
        if (location.value === "All Adelaide, South Australia") {
          return worker.location.includes("South Australia");
        }
        return location.value === worker.location;
      });

    return jobMatch && badgeMatch && availabilityMatch && locationMatch;
  });

  const showSignRegisterInModal = () => {
    setSigninRegisterModalOpen(true);
  };

  const closeSignRegisterInModal = () => {
    setSigninRegisterModalOpen(false);
  };

  const handleRefresh = () => {
    setLoading(true);

    fetchData();
  };

  const isFilterApplied = () => {
    return (
      selectedJobs.length > 0 ||
      selectedBadges.length > 0 ||
      selectedAvailability.length > 0 ||
      selectedLocations.length > 0
    );
  };

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  return (
    <div className="p-4 bg-white">
      {showButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-5 right-5 p-3 rounded-full shadow-lg hover:opacity-90 transition-opacity"
          style={{
            background: "linear-gradient(to right, #6A5ACD, #00BFFF)", // Gradient color
            color: "white",
            zIndex: 1000,
          }}
        >
          <FaArrowUp size={20} /> {/* FontAwesome arrow up icon */}
        </button>
      )}
      <h1 className="text-3xl font-semibold mb-2 text-center">
        Search Jobs That Match Your Skills and Schedule
      </h1>
      <p className="text-gray-500 text-center mb-6">
        Filter by job type, location, certificates, and availability to find the
        best fit.
      </p>

      <div className="flex flex-wrap">
        <Select
          isMulti
          options={jobOptions}
          value={selectedJobs}
          onChange={setSelectedJobs}
          className="border p-2 mb-4 w-80"
          placeholder="Select Jobs (Start Typing..)"
          onInputChange={(inputValue) => setJobInput(inputValue)}
          styles={customStyles}
          isDisabled={loading} // Disable while loading
        />
        {/* <Select
          isMulti
          options={badgeOptions}
          value={selectedBadges}
          onChange={setSelectedBadges}
          onInputChange={(inputValue) => setCertificateInput(inputValue)}
          className="border p-2 mb-4 w-80"
          placeholder="Select Certificates (Start Typing..)"
          styles={customStyles}
          isDisabled={loading} // Disable while loading
        /> */}
        <Select
          isMulti
          options={availabilityOptions}
          value={selectedAvailability}
          onChange={setSelectedAvailability}
          className="border p-2 mb-4 w-80"
          placeholder="Available Days"
          styles={customStyles}
          isDisabled={loading} // Disable while loading
        />
        <Select
          isMulti
          options={locationOptions}
          value={selectedLocations}
          onChange={setSelectedLocations}
          onInputChange={(inputValue) => setLocationInput(inputValue)}
          className="border p-2 mb-4 w-80"
          placeholder="Select Location (Start Typing..)"
          styles={customStyles}
          isDisabled={loading} // Disable while loading
        />
      </div>
      <div className="flex">
        <button
          onClick={handleAdvertiseClick}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4  rounded-full flex items-center gap-2"
          disabled={loading} // Disable while loading
        >
          <FaPlus size={20} />
          Ask for a job
        </button>
        <button
          onClick={handleRefresh}
          className="bg-blue-500 ml-3 hover:bg-blue-700 text-white font-bold py-2 px-4  rounded-full flex items-center gap-2"
          disabled={loading} // Disable while loading
        >
          <FaSync size={20} />
          Refresh
        </button>
      </div>

      {filteredLoading || loading ? (
        <Spinner /> // Show spinner while filtered workers are loading
      ) : (
        <>
          {filteredWorkers.length === 0 ? (
            <div className="text-center my-4 max-h-screen">
              <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
                <p className="text-center sm:text-left">
                  We're onboarding more employers, so new jobs will be available
                  soon. If you don't find a match now, let us know what you're
                  looking for, and employers can reach out when your role is
                  needed.
                </p>

                <button
                  onClick={handleAdvertiseClick}
                  className="text-blue-500 hover:text-blue-700 underline font-bold"
                  disabled={loading} // Disable while loading
                >
                  Ask for a job
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap mb-4">
              {filteredWorkers.map((worker, index) => (
                <div
                  key={index}
                  className="group transform transition-transform duration-300"
                >
                  <AdvisorCard
                    name={worker.name}
                    job={worker.job}
                    phone={worker.phone}
                    email={worker.email}
                    location={worker.location}
                    description={worker.description}
                    certificates={worker.certificates}
                    availability={worker.availability}
                    sharePhone={worker.sharePhone}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onRequestClose={closeFormModal}
        contentLabel="Worker Form"
        className="fixed inset-0 flex items-center justify-center"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
      >
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
          <button
            onClick={closeFormModal}
            className="float-right text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={20} />
          </button>
          <FormForWorker onSubmit={handleFormSubmit} />
        </div>
      </Modal>

      {/* Message Modal */}
      <Modal
        isOpen={showMessageModal}
        onRequestClose={closeMessageModal}
        contentLabel="Submission Status"
        className="fixed inset-0 flex items-center justify-center"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
      >
        <div
          className={`bg-white p-6 rounded-lg shadow-lg w-full max-w-md mx-auto flex flex-col items-center ${
            messageType === "success" ? "border-green-500" : "border-red-500"
          } border`}
        >
          <button
            onClick={closeMessageModal}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={20} />
          </button>
          {messageType === "success" ? (
            <FaCheckCircle size={48} className="text-green-500 mb-4" />
          ) : (
            <FaTimesCircle size={48} className="text-red-500 mb-4" />
          )}
          <h2 className="text-lg font-bold mb-2">
            {messageType === "success" ? "Success" : "Error"}
          </h2>
          <p className="text-center">{message}</p>
          <button
            onClick={closeMessageModal}
            className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Close
          </button>
          {messageType !== "success" && (
            <a href="/feedback" className="mt-2 text-blue-500 hover:underline">
              <button className="mt-2 text-blue-500 hover:underline">
                Feedback
              </button>
            </a>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={signinRegisterModalOpen}
        onRequestClose={closeSignRegisterInModal}
        contentLabel="User Action Dialog"
        className="fixed inset-0 flex items-center justify-center"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
      >
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md mx-auto flex flex-col items-center border-green-500">
          <button
            onClick={closeSignRegisterInModal}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
          <div className="text-yellow-500 text-4xl mb-4">
            <FaUserFriends />
          </div>
          <h2>
            Unlock new career opportunities and find your perfect match! Sign in
            or register now to connect with top employers and discover your next
            great job.{" "}
          </h2>
          <div className="flex">
            <button
              onClick={() => navigate("/login")}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded mt-4 ml-4"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate("/register")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded mt-4 ml-4"
            >
              Register
            </button>
            <button
              onClick={closeSignRegisterInModal}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mt-4 ml-4"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FindWork;
